import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  CheckCircle2,
  Loader2,
  AlertCircle,
  ArrowRight,
} from "lucide-react";
import { BrandMark, BrandWordmark } from "./LandingPage";
import { api } from "../lib/api";
import { saveEntitlement } from "../lib/pro";
import SiteFootnote from "../components/SiteFootnote";

export default function RestoreProPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "verifying" });
  const ranRef = useRef(false);

  useEffect(() => {
    document.title = "Restore Pro Pass — CFO Grade";
    if (ranRef.current) return;
    ranRef.current = true;
    if (!token) {
      setState({ status: "error", message: "Missing recovery token." });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/payments/verify/${token}`);
        if (data?.active) {
          saveEntitlement({ ...data, token });
          setState({ status: "ok", entitlement: data });
        } else if (data?.reason === "expired") {
          setState({ status: "expired" });
        } else {
          setState({ status: "not_found" });
        }
      } catch (e) {
        setState({
          status: "error",
          message: e?.response?.data?.detail || "Could not verify the link.",
        });
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-[#F7F5F0] bg-grain relative" data-testid="restore-page">
      <header className="max-w-5xl mx-auto px-6 sm:px-10 pt-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <BrandWordmark size="md" />
        </Link>
      </header>

      <main className="max-w-2xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
        {state.status === "verifying" && (
          <div className="text-center" data-testid="restore-verifying">
            <Loader2 size={36} strokeWidth={1.5} className="text-[#C26D5C] mx-auto animate-spin" />
            <h1 className="font-serif-display text-4xl text-[#1F382E] mt-6 tracking-tight">
              Verifying your pass…
            </h1>
            <p className="text-[#5C665D] mt-3 leading-relaxed">One second.</p>
          </div>
        )}

        {state.status === "ok" && (
          <div className="text-center" data-testid="restore-ok">
            <CheckCircle2 size={44} strokeWidth={1.5} className="text-[#2C5C43] mx-auto" />
            <h1 className="font-serif-display text-5xl text-[#1F382E] mt-6 tracking-tight">
              Restored.
            </h1>
            <p className="text-[#5C665D] mt-4 leading-relaxed">
              Your {state.entitlement?.scope === "pro" ? "Pro Pass" : "PDF unlock"} is active on
              this device.
              {state.entitlement?.expires_at && (
                <>
                  {" "}
                  Active until{" "}
                  <strong className="text-[#1F382E]">
                    {new Date(state.entitlement.expires_at).toLocaleString()}
                  </strong>.
                </>
              )}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={() => navigate("/analyze")}
                className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-11 px-6 transition-all duration-300"
                data-testid="restore-analyze-btn"
              >
                Run an analysis <ArrowRight size={14} strokeWidth={1.5} className="ml-2" />
              </Button>
              <Link to="/">
                <Button variant="ghost" className="text-[#5C665D]">
                  Back to home
                </Button>
              </Link>
            </div>
          </div>
        )}

        {(state.status === "expired" || state.status === "not_found" || state.status === "error") && (
          <div className="text-center" data-testid="restore-error">
            <AlertCircle size={36} strokeWidth={1.5} className="text-[#A84632] mx-auto" />
            <h1 className="font-serif-display text-4xl text-[#1F382E] mt-6 tracking-tight">
              {state.status === "expired"
                ? "This pass has expired."
                : state.status === "not_found"
                  ? "Recovery link not recognised."
                  : "Couldn't verify the link."}
            </h1>
            <p className="text-[#5C665D] mt-3 leading-relaxed">
              {state.status === "expired"
                ? "Pro Passes are 30 days. Grab a new one to keep going."
                : state.status === "not_found"
                  ? "The link may have been mistyped, or the pass was issued for a different environment."
                  : state.message || "Something went wrong."}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/analyze">
                <Button className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-11 px-6">
                  Run an analysis
                </Button>
              </Link>
              <Link to="/">
                <Button variant="ghost" className="text-[#5C665D]">
                  Back to home
                </Button>
              </Link>
            </div>
          </div>
        )}
      </main>
      <SiteFootnote />
    </div>
  );
}
